import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CalendarRange,
  CheckCircle2,
  CircleSlash,
  TrendingUp,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDate } from "@/lib/format";

interface DailyReport {
  date: string;
  examinations_total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_verdict: Record<string, number>;
}

const STATUS_COLOR: Record<string, string> = {
  open: "gray",
  in_progress: "brand",
  pending_chief: "orange",
  completed: "brand",
  cancelled: "red",
};

export function ReportsPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date>(new Date());

  const { data: daily, isLoading: dailyLoading } = useQuery({
    queryKey: ["report-daily", date.toISOString().slice(0, 10)],
    queryFn: async () => {
      const { data } = await api.get<DailyReport>("/reports/daily", {
        params: { date: date.toISOString().slice(0, 10) },
      });
      return data;
    },
  });

  const total = daily?.examinations_total ?? 0;

  return (
    <Stack gap="lg">
      <PageHeader title={t("reports.title")} subtitle={t("reports.subtitle")} />

      <Card padding="md" radius="xl">
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <CalendarRange size={18} strokeWidth={2} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: "0.06em" }}>
                {t("common.date")}
              </Text>
              <Text size="sm" fw={600}>
                {formatDate(date)}
              </Text>
            </Stack>
          </Group>
          <DateInput
            value={date}
            onChange={(v) => v && setDate(v)}
            valueFormat="DD.MM.YYYY"
            size="xs"
            style={{ width: 180 }}
          />
        </Group>
      </Card>

      <Card
        padding="xl"
        radius="xl"
        style={{
          background:
            "linear-gradient(135deg, var(--mantine-color-brand-7) 0%, var(--mantine-color-brand-9) 100%)",
          color: "white",
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap="xs">
            <Text size="sm" fw={500} style={{ opacity: 0.85 }}>
              {t("reports.hero.total_for_day")}
            </Text>
            <Text size="3rem" fw={800} lh={1}>
              {dailyLoading ? "—" : total}
            </Text>
            <Text size="sm" style={{ opacity: 0.85 }}>
              {formatDate(date)}
            </Text>
          </Stack>
          <ThemeIcon size={64} radius="xl" variant="white" color="brand">
            <TrendingUp size={32} strokeWidth={2} />
          </ThemeIcon>
        </Group>
      </Card>

      <Card padding="xl" radius="xl">
        <Group gap="sm" mb="lg">
          <ThemeIcon size={36} radius="xl" variant="light" color="brand">
            <Activity size={18} strokeWidth={2} />
          </ThemeIcon>
          <Title order={5} fw={700}>
            {t("reports.daily_title")}
          </Title>
        </Group>

        {dailyLoading && <Loader size="sm" />}

        {daily && (
          <Stack gap="lg">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <VerdictStat
                icon={<CheckCircle2 size={22} strokeWidth={2} />}
                label={t("reports.fields.healthy")}
                value={daily.by_verdict.healthy ?? 0}
                total={total}
                color="brand"
              />
              <VerdictStat
                icon={<CircleSlash size={22} strokeWidth={2} />}
                label={t("reports.fields.restricted")}
                value={daily.by_verdict.restricted_fit ?? 0}
                total={total}
                color="orange"
              />
              <VerdictStat
                icon={<XCircle size={22} strokeWidth={2} />}
                label={t("reports.fields.unfit")}
                value={daily.by_verdict.unfit_for_work ?? 0}
                total={total}
                color="red"
              />
            </SimpleGrid>

            {Object.keys(daily.by_status).length > 0 && (
              <Box>
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">
                  {t("reports.fields.by_status")}
                </Text>
                <Group gap="xs">
                  {Object.entries(daily.by_status).map(([status, count]) => (
                    <Badge
                      key={status}
                      color={STATUS_COLOR[status] ?? "gray"}
                      variant="light"
                      size="lg"
                      radius="sm"
                    >
                      {t(`examinations.statuses.${status}`, { defaultValue: status })}: {count}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}

            {Object.keys(daily.by_type).length > 0 && (
              <Box>
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">
                  {t("reports.fields.by_type")}
                </Text>
                <Group gap="xs">
                  {Object.entries(daily.by_type).map(([type, count]) => (
                    <Badge
                      key={type}
                      color="brand"
                      variant="light"
                      size="lg"
                      radius="sm"
                    >
                      {t(`examinations.types.${type}`, { defaultValue: type })}: {count}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
          </Stack>
        )}
      </Card>

    </Stack>
  );
}

function VerdictStat({
  icon,
  label,
  value,
  total,
  color,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Card
      padding="lg"
      radius="lg"
      style={{
        background: `var(--mantine-color-${color}-0)`,
        border: `1px solid var(--mantine-color-${color}-2)`,
      }}
    >
      <Group justify="space-between" align="flex-start" mb="md">
        <ThemeIcon size={42} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Badge color={color} variant="filled" size="sm" radius="sm">
          {pct}%
        </Badge>
      </Group>
      <Text size="xs" fw={700} tt="uppercase" c={`${color}.7`} style={{ letterSpacing: "0.06em" }}>
        {label}
      </Text>
      <Text size="1.8rem" fw={800} c={`${color}.9`} mt={2} lh={1.1}>
        {value}
      </Text>
      <Progress value={pct} color={color} size="sm" radius="xl" mt="md" />
    </Card>
  );
}
